'use client';

import * as React from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

/**
 * @typedef {'center' | 'top' | 'bottom' | 'left' | 'right'} DialogPosition
 */

const DialogContext = React.createContext({ position: 'center' });

const contentVariants = cva(
  'fixed z-50 grid w-full gap-4 bg-white p-6 shadow-lg transition ease-in-out duration-300',
  {
    variants: {
      position: {
        center: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg',
        top: 'top-0 inset-x-0',
        bottom: 'bottom-0 inset-x-0',
        left: 'top-0 left-0 h-full',
        right: 'top-0 right-0 h-full',
      },
    },
    defaultVariants: {
      position: 'center',
    },
  },
);

/**
 * @typedef {object} DialogProps
 * @property {boolean} open - Whether the dialog is open.
 * @property {(open: boolean) => void} onOpenChange - Callback for when the dialog is closed.
 * @property {DialogPosition} [position='center'] - The position of the dialog.
 * @property {React.ReactNode} children - The content of the dialog.
 */

/**
 * The main Dialog component that wraps everything, controlling the open/closed state.
 * @param {DialogProps} props
 */
function Dialog({ open, onOpenChange, position = 'center', children }) {
  return (
    <DialogContext.Provider value={{ position }}>
      <Transition.Root show={open} as={React.Fragment}>
        <HeadlessDialog as="div" className="relative z-50" onClose={onOpenChange}>
          {children}
        </HeadlessDialog>
      </Transition.Root>
    </DialogContext.Provider>
  );
}

/**
 * @typedef {React.ComponentProps<typeof Transition.Child>} DialogOverlayProps
 */

/**
 * A translucent overlay that covers the background content when the dialog is open.
 * @param {DialogOverlayProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 */
function DialogOverlayComponent(props, ref) {
  return (
    <Transition.Child
      as={React.Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      {...props}
    >
      <div ref={ref} className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
    </Transition.Child>
  );
}

/**
 * @typedef {React.ComponentProps<typeof HeadlessDialog.Panel> & { className?: string }} DialogContentProps
 */

/**
 * The main content panel of the dialog. It animates based on the specified position.
 * @param {DialogContentProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 */
function DialogContentComponent({ className, children, ...props }, ref) {
  const { position } = React.useContext(DialogContext);

  const transitionProps = {
    center: {
      enterFrom: 'opacity-0 scale-95',
      enterTo: 'opacity-100 scale-100',
      leaveFrom: 'opacity-100 scale-100',
      leaveTo: 'opacity-0 scale-95',
    },
    top: {
      enterFrom: '-translate-y-full',
      enterTo: 'translate-y-0',
      leaveFrom: 'translate-y-0',
      leaveTo: '-translate-y-full',
    },
    bottom: {
      enterFrom: 'translate-y-full',
      enterTo: 'translate-y-0',
      leaveFrom: 'translate-y-0',
      leaveTo: 'translate-y-full',
    },
    left: {
      enterFrom: '-translate-x-full',
      enterTo: 'translate-x-0',
      leaveFrom: 'translate-x-0',
      leaveTo: '-translate-x-full',
    },
    right: {
      enterFrom: 'translate-x-full',
      enterTo: 'translate-x-0',
      leaveFrom: 'translate-x-0',
      leaveTo: 'translate-x-full',
    },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <Transition.Child
        as={React.Fragment}
        enter="ease-out duration-300"
        leave="ease-in duration-200"
        {...transitionProps[position]}
      >
        <HeadlessDialog.Panel
          ref={ref}
          className={twMerge(contentVariants({ position }), className)}
          {...props}
        >
          {children}
        </HeadlessDialog.Panel>
      </Transition.Child>
    </div>
  );
}

/**
 * @typedef {React.HTMLAttributes<HTMLDivElement>} DialogHeaderProps
 */

/**
 * A container for the dialog's header, typically containing a title and description.
 * @param {DialogHeaderProps} props
 */
function DialogHeader({ className, ...props }) {
  return (
    <div
      className={twMerge(`flex flex-col space-y-1.5 text-center sm:text-left ${className}`)}
      {...props}
    />
  );
}

/**
 * @typedef {React.HTMLAttributes<HTMLDivElement>} DialogFooterProps
 */

/**
 * A container for the dialog's footer, typically containing action buttons.
 * @param {DialogFooterProps} props
 */
function DialogFooter({ className, ...props }) {
  return (
    <div
      className={twMerge(
        `flex flex-col-reverse sm:flex-row sm:justify-between sm:space-x-2 ${className}`,
      )}
      {...props}
    />
  );
}

/**
 * @typedef {React.ComponentProps<typeof HeadlessDialog.Title>} DialogTitleProps
 */

/**
 * The title of the dialog. Should be used inside DialogHeader.
 * @param {DialogTitleProps} props
 * @param {React.Ref<HTMLHeadingElement>} ref
 */
function DialogTitleComponent(props, ref) {
  return (
    <HeadlessDialog.Title
      ref={ref}
      className={twMerge(`text-lg font-semibold leading-none tracking-tight ${props.className}`)}
      {...props}
    />
  );
}

/**
 * @typedef {React.ComponentProps<typeof HeadlessDialog.Description>} DialogDescriptionProps
 */

/**
 * The description or supporting text for the dialog. Should be used inside DialogHeader.
 * @param {DialogDescriptionProps} props
 * @param {React.Ref<HTMLParagraphElement>} ref
 */
function DialogDescriptionComponent(props, ref) {
  return (
    <HeadlessDialog.Description
      ref={ref}
      className={twMerge(`text-sm text-muted-foreground ${props.className}`)}
      {...props}
    />
  );
}

/**
 * @typedef {React.ButtonHTMLAttributes<HTMLButtonElement>} DialogCloseProps
 */

/**
 * A button to close the dialog. It is positioned at the top-right corner by default.
 * @param {DialogCloseProps} props
 * @param {React.Ref<HTMLButtonElement>} ref
 */
function DialogCloseComponent({ className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={twMerge(
        'absolute top-2 right-2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none',
        className,
      )}
      {...props}
    >
      <XMarkIcon className="w-4 h-4" />
      <span className="sr-only">Close</span>
    </button>
  );
}

const DialogOverlay = React.forwardRef(DialogOverlayComponent);
DialogOverlay.displayName = 'DialogOverlay';

const DialogContent = React.forwardRef(DialogContentComponent);
DialogContent.displayName = 'DialogContent';

const DialogTitle = React.forwardRef(DialogTitleComponent);
DialogTitle.displayName = HeadlessDialog.Title.displayName;

const DialogDescription = React.forwardRef(DialogDescriptionComponent);
DialogDescription.displayName = HeadlessDialog.Description.displayName;

const DialogClose = React.forwardRef(DialogCloseComponent);
DialogClose.displayName = 'DialogClose';

DialogHeader.displayName = 'DialogHeader';
DialogFooter.displayName = 'DialogFooter';

export {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
